import React, { useState, useEffect } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  Keyboard,
  Platform,
} from 'react-native';
import { Alert } from '../../utils/toast';
import { StackScreenProps } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { ClubsStackParamList } from '../../navigation/ClubsNavigator';
import { ThemedText } from '../../components/common/ThemedText';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { createMeetup } from '../../lib/api/meetups';
import { getClubById, ClubMember } from '../../lib/api/clubs';
import { CustomPicker } from '../../components/common/CustomPicker';
import { DatePicker } from '../../components/common/DatePicker';
import { TimePicker } from '../../components/common/TimePicker';

type Props = StackScreenProps<ClubsStackParamList, 'SendMeetup'>;

export const SendMeetupScreen: React.FC<Props> = ({ navigation, route }) => {
  const { clubId, recipientId, recipientName } = route.params;
  const { theme } = useTheme();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  
  const [selectedRecipientId, setSelectedRecipientId] = useState(recipientId || '');
  const [place, setPlace] = useState('');
  const [phone, setPhone] = useState(user?.phoneNumber || '');
  const [type, setType] = useState<'One to One' | 'Group' | 'Conclave'>('One to One');
  const [message, setMessage] = useState('');
  const [meetupDate, setMeetupDate] = useState<Date | undefined>(new Date());
  const [meetupTime, setMeetupTime] = useState<Date | undefined>(new Date());

  useEffect(() => {
    loadMembers();

    const keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', () => {
      setKeyboardVisible(true);
    });
    const keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardVisible(false);
    });

    return () => {
      keyboardDidShowListener.remove();
      keyboardDidHideListener.remove();
    };
  }, []);

  const loadMembers = async () => {
    try {
      setLoadingMembers(true);
      const clubData = await getClubById(clubId);
      
      // Filter out current user from members list
      const otherMembers = clubData.members?.filter(m => m.userId !== user?.userId) || [];
      setMembers(otherMembers);
    } catch (error) {
      console.error('Error loading members:', error);
      Alert.alert('Error', 'Failed to load club members');
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleSend = async () => {
    // Validation
    if (!selectedRecipientId) {
      Alert.alert('Error', 'Please select a member');
      return;
    }
    if (!place.trim()) {
      Alert.alert('Error', 'Please enter a place');
      return;
    }
    if (!phone.trim()) {
      Alert.alert('Error', 'Please enter a phone number');
      return;
    }
    if (!meetupDate) {
      Alert.alert('Error', 'Please select a date');
      return;
    }
    if (!meetupTime) {
      Alert.alert('Error', 'Please select a time');
      return;
    }

    try {
      setLoading(true);
      
      // Combine date and time into a single datetime
      const combinedDateTime = new Date(
        meetupDate.getFullYear(),
        meetupDate.getMonth(),
        meetupDate.getDate(),
        meetupTime.getHours(),
        meetupTime.getMinutes(),
        0
      );
      
      await createMeetup({
        clubId,
        recipientId: selectedRecipientId,
        place: place.trim(),
        phone: phone.trim(),
        type,
        dateTime: combinedDateTime.toISOString(),
        message: message.trim() || undefined,
      });

      Alert.alert('Success', 'Meetup request sent successfully', [
        {
          text: 'OK',
          onPress: () => navigation.goBack(),
        },
      ]);
    } catch (error: any) {
      console.error('Error sending meetup:', error);
      Alert.alert('Error', error.message || 'Failed to send meetup request');
    } finally {
      setLoading(false);
    }
  };

  if (loadingMembers) {
    return (
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={[styles.header, { borderBottomColor: theme.border }]}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color={theme.text} />
          </TouchableOpacity>
          <ThemedText style={styles.headerTitle}>Send Meetup Request</ThemedText>
          <View style={{ width: 24 }} />
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={styles.headerTitle}>Send Meetup Request</ThemedText>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView 
        style={styles.content}
        contentContainerStyle={[
          styles.contentContainer,
          keyboardVisible && { paddingBottom: 300 }
        ]}
      >
        {/* Member Selection */}
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Member <ThemedText style={{ color: '#EF4444' }}>*</ThemedText>
          </ThemedText>
          <CustomPicker
            selectedValue={selectedRecipientId}
            onValueChange={(value) => setSelectedRecipientId(value || '')}
            items={[
              { label: 'Select a member', value: '' },
              ...members.map((member) => ({
                label: member.user.name || 'Unknown',
                value: member.userId,
              })),
            ]}
            placeholder="Select a member"
            searchable={true}
            searchPlaceholder="Search members..."
            disabled={!!recipientId}
          />
        </View>

        {/* Place */}
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Place <ThemedText style={{ color: '#EF4444' }}>*</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, borderColor: theme.border, color: theme.text }]}
            placeholder="Enter meeting place"
            placeholderTextColor={theme.textSecondary}
            value={place}
            onChangeText={setPlace}
          />
        </View>

        {/* Phone */}
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Phone <ThemedText style={{ color: '#EF4444' }}>*</ThemedText>
          </ThemedText>
          <TextInput
            style={[styles.input, { backgroundColor: theme.card, borderColor: theme.border, color: theme.text }]}
            placeholder="Enter phone number"
            placeholderTextColor={theme.textSecondary}
            value={phone}
            onChangeText={setPhone}
            keyboardType="phone-pad"
          />
        </View>

        {/* Type */}
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Type <ThemedText style={{ color: '#EF4444' }}>*</ThemedText>
          </ThemedText>
          <CustomPicker
            selectedValue={type}
            onValueChange={(value) => setType((value as 'One to One' | 'Group' | 'Conclave') || 'One to One')}
            items={[
              { label: 'One to One', value: 'One to One' },
              { label: 'Group', value: 'Group' },
              { label: 'Conclave', value: 'Conclave' },
            ]}
            placeholder="Select type"
            searchable={false}
          />
        </View>

        {/* Date Picker */}
        <DatePicker
          value={meetupDate}
          onChange={setMeetupDate}
          label="Date *"
          helperText="Select the meeting date"
          minimumDate={new Date()}
        />

        {/* Time Picker */}
        <TimePicker
          value={meetupTime}
          onChange={setMeetupTime}
          label="Time *"
          helperText="Select the meeting time"
        />

        {/* Message */}
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: theme.text }]}>
            Message (Optional)
          </ThemedText>
          <TextInput
            style={[styles.textArea, { backgroundColor: theme.card, borderColor: theme.border, color: theme.text }]}
            placeholder="Add a message..."
            placeholderTextColor={theme.textSecondary}
            value={message}
            onChangeText={setMessage}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
          />
        </View>

        {/* Send Button */}
        <TouchableOpacity
          style={[styles.sendButton, { backgroundColor: theme.accent }]}
          onPress={handleSend}
          disabled={loading}
          activeOpacity={0.7}
        >
          {loading ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <ThemedText style={styles.sendButtonText}>Send Meetup Request</ThemedText>
          )}
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    ...(Platform.OS !== 'web' ? {
      paddingTop: 48,
    } : {}),
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
  },
  section: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 8,
  },
  input: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  textArea: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    minHeight: 100,
  },
  pickerDoneButton: {
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  pickerDoneText: {
    fontSize: 14,
    fontWeight: '600',
  },
  sendButton: {
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 10,
    marginBottom: 30,
  },
  sendButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
